import { useCallback, useMemo } from 'react';
import { Box, Group, SegmentedControl, Stack, Text, Tooltip as MantineTooltip, useMantineTheme } from '@mantine/core';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import { getDemographicColor, getDemographicLabel } from '../../demographics';
import type { TuAswpData } from '../types';
import { latest, previous } from '../helpers';
import { CardShell } from './CardShell';
import { MetricRow } from './MetricRow';

type Mode = 'race' | 'gender';
type ParticipationCompletionView = 'participation' | 'completion';

type ShareSegment = { key: string; label: string; total: number; color: string; qoqText: string };

type ParticipationCompletionCardProps = {
  data: TuAswpData;
  view: ParticipationCompletionView;
  mode: Mode;
  setMode: (mode: Mode) => void;
};

export function ParticipationCompletionCard({ data, view, mode, setMode }: ParticipationCompletionCardProps) {
  const theme = useMantineTheme();

  const declared = data.declared;
  const curD = latest(declared);
  const prevD = previous(declared);

  const grads = data.graduates;
  const curG = latest(grads);

  const qoqArrow = useCallback((n: number): '↑' | '↓' | '' => (n > 0 ? '↑' : n < 0 ? '↓' : ''), []);

  const tooltipShellStyle = {
    backgroundColor: theme.colorScheme === 'dark' ? '#000' : '#fff',
    border: `1px solid ${theme.colorScheme === 'dark' ? '#333' : '#ddd'}`,
    borderRadius: 10,
    color: theme.colorScheme === 'dark' ? '#fff' : '#000',
    padding: '10px 12px',
    maxWidth: 320,
    whiteSpace: 'normal' as const,
    wordBreak: 'break-word' as const,
  } as const;

  const hintTooltipStyles = {
    tooltip: {
      ...(tooltipShellStyle as any),
      maxWidth: 320,
      fontSize: 13,
      lineHeight: 1.35,
      whiteSpace: 'normal',
      wordBreak: 'break-word',
    },
    arrow: {
      border: `1px solid ${theme.colorScheme === 'dark' ? '#333' : '#ddd'}`,
    },
  } as const;

  const formatQoq = useCallback(
    (_apiFieldPath: string, curCount: number, prevCount: number | null): string => {
      if (prevCount === null) return '— QoQ';
      if (prevCount === 0) return '— QoQ';
      const pct = ((curCount - prevCount) / prevCount) * 100;
      const a = qoqArrow(pct);
      return a ? `${a}${Math.abs(pct).toFixed(2)}% QoQ` : '0.00% QoQ';
    },
    [qoqArrow],
  );

  const raceTotals = useMemo(() => {
    return data.declaredAllTimeByRace;
  }, [data.declaredAllTimeByRace]);

  const genderTotals = useMemo(() => {
    return data.declaredAllTimeByGender;
  }, [data.declaredAllTimeByGender]);

  const raceShareSegments = useMemo(() => {
    const raceColor = (r: string, idx: number): string => {
      return getDemographicColor(theme, idx);
    };
    const raceKeys = Array.from(
      new Set([
        ...Object.keys(raceTotals),
        ...Object.keys(curD.declaredNewByRace ?? {}),
        ...(prevD ? Object.keys(prevD.declaredNewByRace ?? {}) : []),
      ]),
    );
    const filtered = raceKeys.filter((r) => (raceTotals[r] ?? 0) > 0)
      .map((r, idx) => {
        const curCount = curD.declaredNewByRace[r] ?? 0;
        const prevCount = prevD ? prevD.declaredNewByRace[r] ?? 0 : null;
        return {
          key: r,
          label: getDemographicLabel(r),
          total: raceTotals[r] ?? 0,
          color: raceColor(r, idx),
          qoqText: formatQoq(`declaredNewByRace.${r}`, curCount, prevCount),
        };
      })
      .sort((a, b) => b.total - a.total);
    const total = filtered.reduce((acc, s) => acc + s.total, 0);
    return { total, segments: filtered as ShareSegment[] };
  }, [curD, prevD, raceTotals, theme, formatQoq]);

  const genderShareSegments = useMemo(() => {
    const genderKeys = Array.from(
      new Set([
        ...Object.keys(genderTotals),
        ...Object.keys(curD.declaredNewByGender ?? {}),
        ...(prevD ? Object.keys(prevD.declaredNewByGender ?? {}) : []),
      ]),
    );
    const segments = genderKeys.map((g, idx) => {
      const color = getDemographicColor(theme, idx);
      const curCount = curD.declaredNewByGender[g] ?? 0;
      const prevCount = prevD ? prevD.declaredNewByGender[g] ?? 0 : null;
      return {
        key: g,
        label: getDemographicLabel(g),
        total: genderTotals[g],
        color,
        qoqText: formatQoq(`declaredNewByGender.${g}`, curCount, prevCount),
      };
    });
    const total = segments.reduce((acc, s) => acc + s.total, 0);
    return { total, segments: segments as ShareSegment[] };
  }, [curD, prevD, genderTotals, theme, formatQoq]);

  const completionByRace = useMemo(() => {
    const started = data.declaredAllTimeByRace;
    const completed = data.graduatedAllTimeByRace;
    const raceKeys = Array.from(new Set([...Object.keys(started), ...Object.keys(completed)]));

    return raceKeys.map((race) => {
      const s = started[race] ?? 0;
      const c = completed[race] ?? 0;
      const rate = s > 0 ? (c / s) * 100 : 0;
      return { key: race, label: getDemographicLabel(race), rate: Math.round(rate), started: s, completed: c };
    }).sort((a, b) => b.rate - a.rate);
  }, [data.declaredAllTimeByRace, data.graduatedAllTimeByRace]);

  const completionByGender = useMemo(() => {
    const started = data.declaredAllTimeByGender;
    const completed = data.graduatedAllTimeByGender;
    const genderKeys = Array.from(new Set([...Object.keys(started), ...Object.keys(completed)]));

    return genderKeys.map((gender) => {
      const s = started[gender] ?? 0;
      const c = completed[gender] ?? 0;
      const rate = s > 0 ? (c / s) * 100 : 0;
      return { key: gender, label: getDemographicLabel(gender), rate: Math.round(rate), started: s, completed: c };
    }).sort((a, b) => b.rate - a.rate);
  }, [data.declaredAllTimeByGender, data.graduatedAllTimeByGender]);

  const share: { total: number; segments: ShareSegment[] } =
    mode === 'race' ? raceShareSegments : genderShareSegments;

  const extremes = useMemo(() => {
    const segs = share.segments.filter((s) => s.key !== 'Other');
    if (segs.length === 0) return null;
    const sorted = segs.slice().sort((a, b) => b.total - a.total);
    return { most: sorted[0], least: sorted[sorted.length - 1] };
  }, [share.segments]);

  const momentum = useMemo(() => {
    const segs = share.segments.filter((s) => s.key !== 'Other');
    const parse = (txt: string): number | null => {
      if (!txt) return null;
      const m = txt.match(/([0-9]+)%/);
      if (!m) return null;
      const pct = Number(m[1]);
      if (!Number.isFinite(pct)) return null;
      return txt.startsWith('↑') ? pct : txt.startsWith('↓') ? -pct : 0;
    };
    const entries = segs
      .map((s) => ({ key: s.key, label: s.label, qoq: parse(s.qoqText) }))
      .filter((e) => e.qoq !== null) as Array<{ key: string; label: string; qoq: number }>;
    if (entries.length === 0) return null;
    const ups = entries.filter((e) => e.qoq > 0).sort((a, b) => b.qoq - a.qoq);
    const downs = entries.filter((e) => e.qoq < 0).sort((a, b) => a.qoq - b.qoq);
    return { up: ups[0] ?? null, down: downs[0] ?? null };
  }, [share.segments]);

  const completionRows = mode === 'race' ? completionByRace : completionByGender;

  const completionExtremes = useMemo(() => {
    const sorted = completionRows.slice().sort((a, b) => b.rate - a.rate);
    if (sorted.length === 0) return null;
    return { most: sorted[0], least: sorted[sorted.length - 1] };
  }, [completionRows]);

  const title = view === 'participation' ? 'Participation' : 'Completion';
  const subtitle = view === 'participation' ? curD.quarter : curG.quarter;

  return (
    <CardShell title={title} subtitle={subtitle} statusLabel="Snapshot" statusColor="gray">
      <Stack spacing="sm">
        <SegmentedControl
          value={mode}
          onChange={(v) => setMode(v as Mode)}
          data={[
            { label: 'Race', value: 'race' },
            { label: 'Gender', value: 'gender' },
          ]}
          size="xs"
          fullWidth
        />

        {view === 'participation' ? (
          <>
            <MetricRow
              items={[
                {
                  label: 'Highest (Participation)',
                  labelTooltip: `${mode === 'race' ? 'Race' : 'Gender'} category with the highest all-time share of declarations.`,
                  value: (
                    <Text size="sm" weight={800}>
                      {extremes ? extremes.most.label : '—'}
                    </Text>
                  ),
                  sublabel: extremes ? (share.segments.find((s) => s.key === extremes.most.key)?.qoqText ?? '— QoQ') : '—',
                },
                {
                  label: 'Lowest (Participation)',
                  labelTooltip: `${mode === 'race' ? 'Race' : 'Gender'} category with the lowest all-time share of declarations.`,
                  value: (
                    <Text size="sm" weight={800}>
                      {extremes ? extremes.least.label : '—'}
                    </Text>
                  ),
                  sublabel: extremes ? (share.segments.find((s) => s.key === extremes.least.key)?.qoqText ?? '— QoQ') : '—',
                },
                {
                  label: 'Mover',
                  labelTooltip: `Biggest quarter-over-quarter change in declarations for a single ${mode === 'race' ? 'race' : 'gender'} category.`,
                  value: (
                    <Text size="sm" weight={800}>
                      {momentum?.up || momentum?.down ? (momentum.up ?? momentum.down!).label : '—'}
                    </Text>
                  ),
                  sublabel:
                    momentum?.up || momentum?.down
                      ? (() => {
                          const m = momentum.up ?? momentum.down!;
                          const a = qoqArrow(m.qoq);
                          return a ? `${a}${Math.abs(m.qoq).toFixed(2)}% QoQ` : '0.00% QoQ';
                        })()
                      : '—',
                },
              ]}
            />

            <Stack spacing={6} sx={{ marginTop: '0.75rem' }}>
              <MantineTooltip
                withArrow
                withinPortal
                styles={hintTooltipStyles}
                label={
                  mode === 'race'
                    ? 'All-time participation distribution by race categories.'
                    : 'All-time participation distribution by gender.'
                }
              >
                <Text
                  size="xs"
                  color="dimmed"
                  weight={700}
                  sx={{ cursor: 'help', textDecoration: 'underline dotted', textUnderlineOffset: 2, letterSpacing: 0.2 }}
                >
                  Participation By {mode === 'race' ? 'Race' : 'Gender'} (All-Time Share)
                </Text>
              </MantineTooltip>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 16, height: 200 }}>
                <Box sx={{ flex: 1, minWidth: 0, height: '100%' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Tooltip
                        content={({ active, payload }) => {
                          if (!active || !payload || payload.length === 0) return null;
                          const p = payload[0]?.payload as { name: string; value: number; qoqText: string } | undefined;
                          if (!p) return null;
                          const pct = share.total > 0 ? Math.round((p.value / share.total) * 100) : 0;
                          const qoqText = (share.segments.find((s) => s.label === p.name)?.qoqText) ?? '— QoQ';
                          return (
                            <div style={tooltipShellStyle as any}>
                              <div style={{ fontWeight: 700, marginBottom: 6, fontSize: 12, opacity: 0.9 }}>{p.name}</div>
                              <div style={{ fontSize: 12, lineHeight: 1.35 }}>
                                <div style={{ fontWeight: 900, fontSize: 14 }}>{p.value}</div>
                                <div style={{ opacity: 0.9 }}>{pct}% of participants (all-time)</div>
                                <div style={{ opacity: 0.9 }}>{qoqText}</div>
                              </div>
                            </div>
                          );
                        }}
                        contentStyle={{
                          backgroundColor: theme.colorScheme === 'dark' ? '#000' : '#fff',
                          border: `1px solid ${theme.colorScheme === 'dark' ? '#333' : '#ddd'}`,
                          borderRadius: 10,
                          color: theme.colorScheme === 'dark' ? '#fff' : '#000',
                          padding: '10px 12px',
                        }}
                        labelStyle={{ color: theme.colorScheme === 'dark' ? '#fff' : '#000' }}
                        itemStyle={{ color: theme.colorScheme === 'dark' ? '#fff' : '#000' }}
                      />
                      <Pie
                        data={share.segments.filter((s) => s.total > 0).map((s) => ({ name: s.label, value: s.total, qoqText: s.qoqText }))}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={80}
                        startAngle={90}
                        endAngle={-270}
                        paddingAngle={1}
                        stroke="none"
                        isAnimationActive={false}
                      >
                        {share.segments
                          .filter((s) => s.total > 0)
                          .map((s) => (
                            <Cell key={s.key} fill={s.color} stroke="none" />
                          ))}
                      </Pie>
                      <text
                        x="50%"
                        y="50%"
                        textAnchor="middle"
                        dominantBaseline="middle"
                        style={{
                          fontSize: 12,
                          fill: theme.colorScheme === 'dark' ? '#fff' : '#000',
                          fontWeight: 600,
                        }}
                      >
                        All-Time
                      </text>
                    </PieChart>
                  </ResponsiveContainer>
                </Box>
                <Box
                  sx={{
                    width: '50%',
                    display: 'grid',
                    gridTemplateColumns: share.segments.filter((s) => s.total > 0).length > 4 ? '1fr 1fr' : '1fr',
                    gap: '6px 8px',
                    alignContent: 'center',
                  }}
                >
                  {share.segments
                    .filter((s) => s.total > 0)
                    .map((seg) => (
                      <Box key={seg.key} sx={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <Box
                          sx={{
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            background: seg.color,
                            flex: '0 0 auto',
                          }}
                        />
                        <Text size="xs" color="dimmed">
                          {seg.label}
                        </Text>
                      </Box>
                    ))}
                </Box>
              </Box>
            </Stack>
          </>
        ) : (
          <Stack spacing={6}>
            <MantineTooltip
              withArrow
              withinPortal
              styles={hintTooltipStyles}
              label={`All-time completion rate by ${mode === 'race' ? 'race' : 'gender'}: Graduates ÷ Declarations.`}
            >
              <Text
                size="xs"
                color="dimmed"
                sx={{ cursor: 'help', textDecoration: 'underline dotted', textUnderlineOffset: 2 }}
              >
                Completion Rate By {mode === 'race' ? 'Race' : 'Gender'} (All-Time)
              </Text>
            </MantineTooltip>

            {completionExtremes ? (
              <MetricRow
                items={[
                  {
                    label: 'Highest',
                    labelTooltip: `Highest completion rate (${mode === 'race' ? 'race' : 'gender'}).`,
                    value: (
                      <Text size="sm" weight={800}>
                        {completionExtremes.most.label}
                      </Text>
                    ),
                    sublabel: `${completionExtremes.most.rate}%`,
                  },
                  {
                    label: 'Lowest',
                    labelTooltip: `Lowest completion rate (${mode === 'race' ? 'race' : 'gender'}).`,
                    value: (
                      <Text size="sm" weight={800}>
                        {completionExtremes.least.label}
                      </Text>
                    ),
                    sublabel: `${completionExtremes.least.rate}%`,
                  },
                ]}
              />
            ) : null}

            <Stack spacing={10} sx={{ marginTop: '1.5rem' }}>
              {completionRows.map((row) => {
                const pct = Math.max(0, Math.min(100, row.rate));
                return (
                  <Group key={row.key} position="apart" noWrap align="center">
                    <Text size="xs" color="dimmed" sx={{ width: 150 }}>
                      {row.label}
                    </Text>
                    <MantineTooltip
                      withArrow
                      withinPortal
                      styles={hintTooltipStyles}
                      label={
                        <div>
                          <div style={{ fontWeight: 700, marginBottom: 6, fontSize: 12, opacity: 0.9 }}>{row.label}</div>
                          <div style={{ fontSize: 12, lineHeight: 1.35 }}>
                            <div style={{ fontWeight: 900, fontSize: 14 }}>{row.rate}%</div>
                            <div style={{ opacity: 0.9 }}>
                              {row.completed} of {row.started} completed (all-time)
                            </div>
                          </div>
                        </div>
                      }
                    >
                      <Box
                        sx={{
                          position: 'relative',
                          height: 18,
                          flex: 1,
                          borderRadius: 999,
                          backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[5] : theme.colors.gray[2],
                          overflow: 'hidden',
                          cursor: 'default',
                        }}
                      >
                        <Box
                          sx={{
                            width: `${pct}%`,
                            height: '100%',
                            backgroundColor: theme.colors.teal[6],
                            borderRadius: 999,
                          }}
                        />
                        <Text
                          size={10}
                          weight={700}
                          sx={{
                            position: 'absolute',
                            right: 8,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            color: theme.colorScheme === 'dark' ? theme.colors.dark[0] : theme.colors.dark[7],
                          }}
                        >
                          {pct}%
                        </Text>
                      </Box>
                    </MantineTooltip>
                  </Group>
                );
              })}
            </Stack>
          </Stack>
        )}
      </Stack>
    </CardShell>
  );
}
